import React from 'react';
import { 
  Globe, Lock, Copy, Trash2, Edit, ChevronRight, 
  Layers, User
} from 'lucide-react';
import { QuestionSet } from '../../types';

interface QuestionSetCardProps {
  set: QuestionSet;
  isMine: boolean;
  onOpen: (set: QuestionSet) => void;
  onEdit?: (set: QuestionSet) => void;
  onDelete?: (set: QuestionSet) => void;
  onClone?: (set: QuestionSet) => void;
  cloning?: boolean;
}

export const QuestionSetCard: React.FC<QuestionSetCardProps> = ({
  set,
  isMine,
  onOpen,
  onEdit,
  onDelete,
  onClone,
  cloning = false
}) => {
  const getSubjectColor = (subj: string) => {
    switch (subj) {
      case 'Toán':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Ngữ văn':
      case 'Tiếng Việt':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Ngoại ngữ':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Vật lí':
      case 'Hóa học':
      case 'Sinh học':
      case 'Khoa học tự nhiên':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Lịch sử':
      case 'Địa lí':
      case 'Lịch sử và Địa lí':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      case 'Tin học':
      case 'Công nghệ':
        return 'bg-cyan-50 text-cyan-700 border-cyan-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  // Author details extraction
  const authorName = (typeof set.userId === 'object' && set.userId?.displayName)
    ? set.userId.displayName
    : (set.userName || set.user?.displayName || 'Thầy/Cô');

  const authorAvatar = (typeof set.userId === 'object' && (set.userId?.avatar || set.userId?.photoURL))
    ? (set.userId.avatar || set.userId.photoURL)
    : (set.userAvatar || set.user?.avatar || set.user?.photoURL);

  const cloneCount = typeof set.cloneCount === 'number' ? set.cloneCount : 0;

  return (
    <div 
      className="bg-white rounded-2xl border border-slate-200/90 hover:border-[#236539]/40 shadow-xs hover:shadow-md transition-all flex flex-col justify-between overflow-hidden group"
    >
      {/* Top Content Section */}
      <div className="p-5 space-y-3.5">
        {/* Badges row */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center space-x-1.5 flex-wrap gap-y-1">
            {/* Subject badge */}
            <span className={`px-2.5 py-1 text-xs font-bold rounded-lg border ${getSubjectColor(set.subject)}`}>
              {set.subject}
            </span>

            {/* Grade badge */}
            <span className="px-2 py-1 text-xs font-semibold bg-slate-100 text-slate-700 rounded-lg border border-slate-200">
              {isNaN(Number(set.grade)) ? set.grade : `Lớp ${set.grade}`}
            </span>
          </div>

          {/* Public / Private Pill */}
          {set.isPublic ? (
            <span className="inline-flex items-center space-x-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/70">
              <Globe className="w-3 h-3" />
              <span>Công khai</span>
            </span>
          ) : (
            <span className="inline-flex items-center space-x-1 text-[11px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
              <Lock className="w-3 h-3" />
              <span>Riêng tư</span>
            </span>
          )}
        </div>

        {/* Set Title */}
        <h4 
          onClick={() => onOpen(set)}
          className="text-base font-bold text-slate-900 line-clamp-2 cursor-pointer group-hover:text-[#236539] transition-colors leading-snug min-h-[44px]"
          title={set.title || set.name}
        >
          {set.title || set.name}
        </h4>

        {/* Set Description */}
        {set.description && (
          <p 
            className="text-xs text-gray-500 line-clamp-2 leading-relaxed"
            title={set.description}
          >
            {set.description}
          </p>
        )}

        {/* Clone note if cloned */}
        {set.clonedFromName && (
          <p className="text-[11px] text-slate-500 italic line-clamp-1 flex items-center space-x-1">
            <Copy className="w-3 h-3 shrink-0 text-slate-400" />
            <span>Sao chép từ: {set.clonedFromName}</span>
          </p>
        )}

        {/* Author Info & Clone Count Meta Row - ONLY SHOW FOR COMMUNITY LIBRARY */}
        {!isMine && (
          <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
            {/* Author profile */}
            <div className="flex items-center space-x-2 min-w-0 pr-1">
              {authorAvatar ? (
                <img 
                  src={authorAvatar} 
                  alt={authorName} 
                  className="w-6 h-6 rounded-full object-cover ring-1 ring-slate-200 shrink-0" 
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#236539] to-emerald-700 text-white font-bold text-[10px] flex items-center justify-center shrink-0 shadow-2xs">
                  {authorName.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="font-semibold text-slate-700 truncate text-xs group-hover:text-slate-900" title={authorName}>
                {authorName}
              </span>
            </div>

            {/* Clone count badge */}
            <div 
              className="inline-flex items-center space-x-1 text-[11px] font-medium text-slate-600 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-200/80 shrink-0"
              title={`${cloneCount} lượt nhân bản`}
            >
              <Copy className="w-3 h-3 text-[#236539]" />
              <span><strong className="font-bold text-slate-800">{cloneCount}</strong> nhân bản</span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Footer Section */}
      <div className="px-5 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between gap-2">
        {/* Question Counter & Clone Count (for My Library) */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1.5 text-xs text-slate-600 font-semibold">
            <Layers className="w-4 h-4 text-[#236539]" />
            <span><strong className="text-slate-900 font-bold">{set.questionCount || 0}</strong> câu hỏi</span>
          </div>
          
          {isMine && cloneCount > 0 && (
            <div 
              className="flex items-center space-x-1 text-xs text-slate-600 font-semibold border-l border-slate-200 pl-3"
              title={`${cloneCount} lượt nhân bản`}
            >
              <Copy className="w-3.5 h-3.5 text-[#236539]" />
              <span>{cloneCount}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-1.5">
          {isMine ? (
            <>
              {onEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(set)}
                  title="Chỉnh sửa bộ câu hỏi"
                  className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-200/60 rounded-lg transition-colors cursor-pointer"
                >
                  <Edit className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(set)}
                  title="Xóa bộ câu hỏi"
                  className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onOpen(set)}
                className="px-3 py-1.5 bg-[#236539] hover:bg-[#1b4f2c] text-white text-xs font-bold rounded-lg shadow-2xs transition-all flex items-center space-x-1 cursor-pointer"
              >
                <span>Mở bộ</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onOpen(set)}
                className="px-2.5 py-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 text-xs font-bold rounded-lg transition-colors cursor-pointer"
              >
                Xem
              </button>

              {onClone && (
                <button
                  type="button"
                  disabled={cloning}
                  onClick={() => onClone(set)}
                  className="px-3 py-1.5 bg-[#236539] hover:bg-[#1b4f2c] text-white text-xs font-bold rounded-lg shadow-2xs transition-all flex items-center space-x-1.5 cursor-pointer active:scale-95"
                >
                  <Copy className="w-3.5 h-3.5" />
                  <span>Lưu về thư viện</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
